import {MbscCalendarEvent} from "@mobiscroll/react"

/*
   Timeline lane assignment.

   Mobiscroll stacks time-overlapping timeline events into lanes on its own, but it counts two
   things as an overlap that are not one for us:
     - buffer / preparation time, which makes two back-to-back reservations collide, and
     - the transparent block-period overlays, which sit exactly on those buffer windows.
   Both cases pushed ordinary reservations into half-height lanes, so the calendar used to pin
   every timeline event to the full row height in CSS. That hid the real case: two reservations
   genuinely running at the same time on one resource (a spa area with capacity > 1) were drawn
   exactly on top of each other and the lower one was invisible and unclickable.

   So we compute the lanes ourselves from the booked times only and let the CSS position them,
   see the `calendar-lane` rules in ReservationTimelineTab.css.
*/

/** Marker class; the lane CSS only applies to events that carry it. */
const LANE_CLASS = 'calendar-lane'

/**
 * Height one lane needs to show a full two-line card. The timeline runs with rowHeight:'equal',
 * so all rows grow together — which is why this is deliberately just under half the default row
 * height: the common case of two overlapping reservations then costs no extra height at all.
 */
export const LANE_HEIGHT_PX = 40
export const DEFAULT_ROW_HEIGHT_PX = 80

/**
 * Rows never grow past this (four full lanes). There is deliberately NO cap on the number of
 * lanes: a shared resource (a spa area, the "Unassigned" row) can hold any number of bookings at
 * once, and every one of them must stay visible. Past four lanes the lanes get thinner instead of
 * the rows taller — with rowHeight:'equal' a taller row would stretch every row on the page, and a
 * row that grows with its content is what rowHeight:'variable' does, which froze the calendar.
 * The cards switch to compact layouts as their lane shrinks, see ReservationEventPreview.
 */
export const MAX_ROW_HEIGHT_PX = 4 * LANE_HEIGHT_PX

interface LaneAssignment {
    lane: number
    lanes: number
}

const toTime = (value: MbscCalendarEvent['start']): number => {
    if (value instanceof Date) return value.getTime()
    if (typeof value === 'number') return value
    if (typeof value === 'string') return new Date(value).getTime()
    return NaN
}

/** Timeline events always carry a single resource; an array can only come from external data. */
const resourceKey = (event: MbscCalendarEvent): string => {
    const resource = event.resource
    if (Array.isArray(resource)) return String(resource[0] ?? '')
    return String(resource ?? '')
}

/**
 * Greedy interval colouring, one resource at a time. Events are grouped into clusters of
 * transitively overlapping events so that a lone reservation next to a busy cluster still gets
 * the full row height instead of inheriting the cluster's lane count.
 */
const assignLanes = (
    entries: {index: number, start: number, end: number}[],
    assignments: Map<number, LaneAssignment>
) => {
    const ordered = [...entries].sort((a, b) => (a.start - b.start) || (a.end - b.end))

    let cluster: {index: number, lane: number}[] = []
    let laneEnds: number[] = []
    let clusterEnd = -Infinity

    const closeCluster = () => {
        if (!cluster.length) return

        const lanes = laneEnds.length
        cluster.forEach(({index, lane}) => {
            assignments.set(index, {lane, lanes})
        })

        cluster = []
        laneEnds = []
        clusterEnd = -Infinity
    }

    ordered.forEach(entry => {
        // A gap to everything seen so far starts a fresh cluster with its own lane count.
        if (entry.start >= clusterEnd) closeCluster()

        let lane = laneEnds.findIndex(end => end <= entry.start)
        if (lane === -1) {
            lane = laneEnds.length
            laneEnds.push(entry.end)
        } else {
            laneEnds[lane] = entry.end
        }

        cluster.push({index: entry.index, lane})
        clusterEnd = Math.max(clusterEnd, entry.end)
    })

    closeCluster()
}

/**
 * Returns the events with their lane classes attached, plus the highest lane count on the page
 * so the caller can give the rows enough height for it.
 */
export const withTimelineEventLanes = (
    events: MbscCalendarEvent[]
): {events: MbscCalendarEvent[], laneCount: number} => {
    const byResource = new Map<string, {index: number, start: number, end: number}[]>()

    events.forEach((event, index) => {
        // Block periods are background overlays, not bookings — they must not consume a lane.
        if (event.isBlockPeriod) return

        const start = toTime(event.start)
        const end = toTime(event.end)
        if (!isFinite(start) || !isFinite(end)) return

        const key = resourceKey(event)
        const entries = byResource.get(key)
        if (entries) {
            entries.push({index, start, end})
        } else {
            byResource.set(key, [{index, start, end}])
        }
    })

    const assignments = new Map<number, LaneAssignment>()
    byResource.forEach(entries => assignLanes(entries, assignments))

    let laneCount = 1
    assignments.forEach(({lanes}) => {
        if (lanes > laneCount) laneCount = lanes
    })

    return {
        events: events.map((event, index) => {
            const assignment = assignments.get(index)
            if (!assignment) return event

            const laneClasses = [
                LANE_CLASS,
                `${LANE_CLASS}--i${assignment.lane}`,
                `${LANE_CLASS}--n${assignment.lanes}`
            ].join(' ')

            return {
                ...event,
                cssClass: [event.cssClass, laneClasses].filter(Boolean).join(' ')
            }
        }),
        laneCount
    }
}

/**
 * Row height for `laneCount` lanes: never below the default row height, never above
 * MAX_ROW_HEIGHT_PX (more lanes than that make the lanes thinner, not the rows taller).
 */
export const timelineRowHeight = (laneCount: number): number =>
    Math.min(MAX_ROW_HEIGHT_PX, Math.max(DEFAULT_ROW_HEIGHT_PX, laneCount * LANE_HEIGHT_PX))

/**
 * The lane classes carry no geometry themselves: Mobiscroll events only accept a `cssClass`, and
 * the lane count is unbounded, so the rules mapping `calendar-lane--n{count}` / `--i{index}` onto
 * the CSS variables used in ReservationTimelineTab.css are generated for the lanes on the page.
 */
export const timelineLaneStyles = (laneCount: number): string => {
    const rules: string[] = []
    for (let i = 1; i <= laneCount; i++) {
        rules.push(`.${LANE_CLASS}--n${i}{--calendar-lane-count:${i}}`)
        rules.push(`.${LANE_CLASS}--i${i - 1}{--calendar-lane-index:${i - 1}}`)
    }
    return rules.join('\n')
}
